import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const pollingIntervalRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());

  // Load unread count from API
  const loadUnreadCount = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getUnreadNotificationCount();
      setUnreadCount(response.unreadCount || 0);
      lastUpdateRef.current = Date.now();
      console.log('[NotificationContext] Updated unread count:', response.unreadCount || 0);
    } catch (error) {
      console.error('[NotificationContext] Failed to load unread count:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Force refresh notifications (для использования после переводов)
  const refreshNotifications = useCallback(async (delay = 1500) => {
    console.log('[NotificationContext] Force refreshing notifications with delay:', delay);
    
    // Добавляем небольшую задержку чтобы backend успел создать уведомления
    setTimeout(async () => {
      await loadUnreadCount();
    }, delay);
  }, [loadUnreadCount]);

  // Start automatic polling
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Initial load
    loadUnreadCount();
    
    // Set up polling every 30 seconds
    pollingIntervalRef.current = setInterval(() => {
      // Only poll if no recent manual refresh
      const timeSinceLastUpdate = Date.now() - lastUpdateRef.current;
      if (timeSinceLastUpdate >= 25000) { // 25 seconds buffer
        loadUnreadCount();
      }
    }, 30000);
    
    console.log('[NotificationContext] Started polling notifications');
  }, [loadUnreadCount]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('[NotificationContext] Stopped polling notifications');
    }
  }, []);

  // Update unread count manually (for mark as read operations)
  const updateUnreadCount = useCallback((newCount) => {
    setUnreadCount(newCount);
    lastUpdateRef.current = Date.now();
  }, []);

  // Decrement unread count (when marking single notification as read)
  const decrementUnreadCount = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1));
    lastUpdateRef.current = Date.now();
  }, []);

  const value = {
    unreadCount,
    isLoading,
    loadUnreadCount,
    refreshNotifications,
    startPolling,
    stopPolling,
    updateUnreadCount,
    decrementUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 